import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { useCallback, useMemo, useState, useEffect } from 'react';
import Button from '../button/component';
import Form, { FormData } from '../../ui/Form';

const useStyles = makeStyles(() => ({
  formContainer: {
    marginTop: 23, // 35px (old title margin) - 12px (Form title margin)
  },
  buttonContainer: {
    marginTop: 24,
  },
}));

interface RiskFormInputs {
  answer?: string;
}

interface SubmitData {
  answer?: {
    id: string;
    text: string;
  };
}

interface RiskFormProps {
  id: string;
  next: (data: SubmitData) => void;
  options: {
    id: string;
    text: string;
  }[];
  position: number;
  previous: (data: SubmitData) => void;
  question: string;
  total: number;
  value?: string;
}

function RiskForm({
  id,
  next,
  options,
  position,
  previous,
  question,
  total,
  value,
}: RiskFormProps) {
  const classes = useStyles();

  const [selectedAnswer, setSelectedAnswer] = useState<string | undefined>(
    value,
  );

  // Sync external value prop changes
  useEffect(() => {
    setSelectedAnswer(value);
  }, [value]);

  const schema = useMemo(
    () => ({
      type: 'object' as const,
      title: question,
      properties: {
        answer: {
          type: 'string' as const,
          oneOf: options.map((option) => ({
            const: option.id,
            title: option.text,
          })),
        },
      },
    }),
    [question, options],
  );

  const uiSchema = useMemo(
    () => ({
      answer: {
        'ui:widget': 'radio',
        'ui:label': false,
        'ui:options': {
          radioBoxes: true,
        },
      },
      'ui:submitButtonOptions': { norender: true },
    }),
    [],
  );

  const formData = useMemo<RiskFormInputs>(
    () => ({
      answer: selectedAnswer,
    }),
    [selectedAnswer],
  );

  const handleChange = useCallback((data: FormData<RiskFormInputs>) => {
    if (data.formData?.answer) {
      setSelectedAnswer(data.formData.answer);
    }
  }, []);

  const getAnswerData = useCallback((): SubmitData => {
    if (!selectedAnswer) {
      return {};
    }
    const selected = options.find((option) => option.id === selectedAnswer);
    if (!selected) {
      return {};
    }
    return {
      answer: {
        id: selected.id,
        text: selected.text,
      },
    };
  }, [selectedAnswer, options]);

  const onNext = useCallback(() => {
    next(getAnswerData());
  }, [next, getAnswerData]);

  const onPrevious = useCallback(() => {
    previous(getAnswerData());
  }, [previous, getAnswerData]);

  const noopSubmit = useCallback(() => {}, []);

  return (
    <>
      <div className={classes.formContainer}>
        <Form
          key={id}
          schema={schema}
          ui={uiSchema}
          formData={formData}
          onSubmit={noopSubmit}
          onChange={handleChange}
        />
      </div>

      <Grid
        container
        justifyContent="space-between"
        className={classes.buttonContainer}
      >
        <Grid item>
          <Button
            disabled={position <= 1}
            type="button"
            name="previous"
            data-testid="atr-back-button"
            variant="secondary"
            onClick={onPrevious}
          >
            Back
          </Button>
        </Grid>
        <Grid item>
          <Button
            disabled={!selectedAnswer}
            type="button"
            name="next"
            data-testid="atr-next-button"
            onClick={onNext}
          >
            {position === total ? 'See results' : 'Next question'}
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export default RiskForm;
